import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Heart, Sparkles, Loader2, Activity, Trophy } from "lucide-react";
import PremiumToolsSection from "@/components/PremiumToolsSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRIMESTER_LABEL = (t) => (t === 1 ? "Trimester 1" : t === 2 ? "Trimester 2" : "Trimester 3");

const TRIMESTER_TINT = {
  1: "from-pink-500/15 to-rose-500/5 border-pink-400/20",
  2: "from-purple-500/15 to-pink-500/5 border-purple-400/20",
  3: "from-amber-500/15 to-pink-500/5 border-amber-400/20",
};

const trimester = (week) => (week <= 13 ? 1 : week <= 27 ? 2 : 3);

export default function PregnancyExplorer() {
  const { t, i18n } = useTranslation();
  const [allWeeks, setAllWeeks] = useState([]);
  const [activeWeek, setActiveWeek] = useState(13);
  const [content, setContent] = useState(null);
  const [loadingDirectory, setLoadingDirectory] = useState(true);
  const [loadingWeek, setLoadingWeek] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/wellness/pregnancy/all-weeks`)
      .then((r) => setAllWeeks(r.data?.weeks || []))
      .finally(() => setLoadingDirectory(false));
  }, []);

  useEffect(() => {
    setLoadingWeek(true);
    const lang = (i18n.language || "en").split("-")[0];
    axios
      .get(`${API}/wellness/pregnancy/week/${activeWeek}`, { params: { lang } })
      .then((r) => setContent(r.data))
      .catch(() => setContent(null))
      .finally(() => setLoadingWeek(false));
  }, [activeWeek, i18n.language]);

  const min = 1;
  const max = 42;
  const goPrev = () => setActiveWeek((w) => Math.max(min, w - 1));
  const goNext = () => setActiveWeek((w) => Math.min(max, w + 1));

  const ringByTrimester = useMemo(() => {
    const groups = { 1: [], 2: [], 3: [] };
    allWeeks.forEach((w) => groups[trimester(w.week)].push(w));
    return groups;
  }, [allWeeks]);

  return (
    <div className="space-y-8" data-testid="pregnancy-explorer-page">
      <motion.header
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/15 border border-pink-400/20 text-pink-200 text-xs uppercase tracking-widest mb-3">
          <Heart className="w-3.5 h-3.5" /> Pregnancy Explorer
        </div>
        <h1 className="text-3xl sm:text-4xl font-serif text-white">All 42 weeks at a glance</h1>
        <p className="text-mystic-200 text-sm mt-2">
          Tap a week to see baby's size, what's happening, and how a partner can support.
        </p>
      </motion.header>

      {/* Hero */}
      <motion.div
        key={activeWeek}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <Card
          className={`bg-gradient-to-br ${TRIMESTER_TINT[trimester(activeWeek)]} backdrop-blur`}
          data-testid="pregnancy-explorer-hero"
        >
          <CardContent className="p-6 sm:p-10">
            {loadingWeek || !content ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-7 h-7 text-pink-300 animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <div>
                  <p className="text-xs uppercase tracking-widest text-pink-200 mb-2">
                    {t("partner.weekTrimester", { week: content.week, trimester: trimester(content.week) })}
                  </p>
                  <h2 className="text-3xl sm:text-4xl font-serif text-white leading-tight">
                    {content.title || content.size || "Tiny wonder"}
                  </h2>
                  {content.baby_length && (
                    <p className="text-mystic-200 text-sm mt-2">
                      {content.baby_length} · {content.baby_weight}
                    </p>
                  )}
                  <div className="mt-4 space-y-3">
                    {content.highlight && (
                      <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                        <p className="text-xs uppercase tracking-wider text-pink-200 mb-1 flex items-center gap-1.5">
                          <Sparkles className="w-3 h-3" /> {t("partner.thisWeek")}
                        </p>
                        <p className="text-white/90 text-sm leading-relaxed">{content.highlight}</p>
                      </div>
                    )}
                    {content.tip && (
                      <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                        <p className="text-xs uppercase tracking-wider text-rose-200 mb-1 flex items-center gap-1.5">
                          <Heart className="w-3 h-3" /> {t("partner.howToSupport", { name: "her" })}
                        </p>
                        <p className="text-white/90 text-sm leading-relaxed">{content.tip}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-center justify-center text-center">
                  {content.image_url ? (
                    <img
                      src={content.image_url}
                      alt={content.baby_size}
                      className="w-48 h-48 sm:w-64 sm:h-64 object-cover rounded-3xl shadow-2xl border border-white/10"
                    />
                  ) : (
                    <div className="w-48 h-48 sm:w-64 sm:h-64 rounded-3xl bg-white/5 border border-white/10 flex items-center justify-center text-7xl">
                      {content.size_emoji || content.baby_size_emoji || "🌱"}
                    </div>
                  )}
                  {content.baby_size_emoji && (
                    <p className="text-3xl mt-3" aria-hidden>{content.baby_size_emoji}</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mt-6">
              <Button
                onClick={goPrev}
                disabled={activeWeek === min}
                variant="outline"
                size="sm"
                data-testid="explorer-prev-btn"
                className="border-white/20 text-white hover:bg-white/5 rounded-full"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Prev
              </Button>
              <span className="text-xs text-mystic-300">
                Week {activeWeek} / {max}
              </span>
              <Button
                onClick={goNext}
                disabled={activeWeek === max}
                variant="outline"
                size="sm"
                data-testid="explorer-next-btn"
                className="border-white/20 text-white hover:bg-white/5 rounded-full"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Directory */}
      {loadingDirectory ? (
        <div className="text-center py-10">
          <Loader2 className="w-6 h-6 text-pink-300 animate-spin inline-block" />
        </div>
      ) : (
        [1, 2, 3].map((tri) => (
          <section key={tri} data-testid={`explorer-trimester-${tri}`}>
            <h3 className="text-mystic-200 text-sm uppercase tracking-widest mb-3">{TRIMESTER_LABEL(tri)}</h3>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory">
              {ringByTrimester[tri].map((w) => {
                const isActive = w.week === activeWeek;
                return (
                  <button
                    key={w.week}
                    onClick={() => setActiveWeek(w.week)}
                    data-testid={`explorer-week-${w.week}`}
                    className={`shrink-0 snap-start w-32 sm:w-36 rounded-2xl p-4 border text-left transition-all ${
                      isActive
                        ? "bg-pink-500/20 border-pink-400/50 ring-2 ring-pink-400/40 scale-[1.02]"
                        : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                    }`}
                  >
                    <p className="text-xs text-mystic-300">Week {w.week}</p>
                    <p className="text-3xl my-2" aria-hidden>{w.baby_size_emoji}</p>
                    <p className="text-white text-sm font-medium leading-tight">{w.title}</p>
                  </button>
                );
              })}
            </div>
          </section>
        ))
      )}

      <PremiumToolsSection
        tools={[
          {
            icon: Activity,
            label: "Kick Counter",
            description: "Log baby's daily kicks",
            to: "/dashboard",
            accent: "bg-pink-500/20 text-pink-300",
            testId: "premium-tool-kick-counter",
          },
          {
            icon: Sparkles,
            label: "Baby Name Generator",
            description: "AI-curated names with origins",
            to: "/dashboard",
            accent: "bg-purple-500/20 text-purple-300",
            testId: "premium-tool-baby-names",
          },
          {
            icon: Trophy,
            label: "Pregnancy Milestones",
            description: "Heartbeat, viability, full-term & more",
            to: "/dashboard",
            accent: "bg-amber-500/20 text-amber-300",
            testId: "premium-tool-milestones",
          },
        ]}
      />

      <section data-testid="explorer-issue-helper-link">
        <Link to="/pregnancy/issues" className="block">
          <Card className="glass border-rose-400/20 hover:border-rose-400/40 transition-all cursor-pointer group">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-rose-200 text-xs uppercase tracking-widest mb-1">Free for everyone</p>
                <p className="text-white font-medium">Issue Helper — gentle remedies for common pregnancy issues →</p>
              </div>
              <Heart className="w-6 h-6 text-rose-300 group-hover:scale-110 transition-transform" />
            </CardContent>
          </Card>
        </Link>
      </section>
    </div>
  );
}
